/**
 * HubSpot API Service
 * Client-side API calls for HubSpot integration
 */

import { api } from './api'
import type {
  HubSpotConnection,
  HubSpotBlogPost,
  HubSpotPage,
  HubSpotKnowledgeArticle,
  HubSpotContentMatchResult,
  PushSchemaToHubSpotRequest,
  ApiResponse
} from 'aeo-schema-generator-shared/types'

export const hubspotApi = {
  /**
   * Handle OAuth callback (exchange code for tokens)
   * Supports both authenticated (SuperSchema-first) and unauthenticated (Marketplace-first) flows
   * @param code - OAuth authorization code from HubSpot
   * @param authToken - Clerk auth token (optional for marketplace-first flow)
   * @param redirectUri - OAuth redirect URI
   * @param state - State parameter for CSRF protection
   * @param matchDomain - Onboarding domain to match against the portal's own pages, if any
   */
  handleCallback: async (
    code: string,
    authToken: string | null,
    redirectUri?: string,
    state?: string | null,
    matchDomain?: string | null,
    /** The onboarding session this connect came from, so the funnel can record the moment against it. */
    sessionId?: string | null
  ): Promise<ApiResponse<{
    connectionId?: string
    portalId: number
    portalName: string
    scopes: string[]
    requiresSignup?: boolean
    state?: string
    flow?: 'authenticated' | 'pending'
    matchedDomain?: string | null
  }>> => {
    const headers: Record<string, string> = {}
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await api.post('/hubspot/callback', {
      code,
      redirectUri,
      state,
      matchDomain: matchDomain || undefined,
      sessionId: sessionId || undefined
    }, {
      headers
    })
    return response.data
  },

  /**
   * Handle seamless install from HubSpot Marketplace (new secure flow)
   * Exchanges code for tokens and stores connection without requiring user account
   *
   * This is the new HubSpot seamless install flow (2025 update) where:
   * 1. HubSpot sends user to install URL with code + returnUrl
   * 2. We exchange the code for tokens
   * 3. We store a standalone connection (no user account needed)
   * 4. We redirect user back to returnUrl
   *
   * @param code - OAuth authorization code from HubSpot
   * @param returnUrl - HubSpot URL to redirect user back to after install
   * @param redirectUri - OAuth redirect URI used for token exchange
   * @see https://developers.hubspot.com/docs/apps/developer-platform/list-apps/understand-app-install-flow
   */
  handleSeamlessInstall: async (
    code: string,
    returnUrl: string,
    redirectUri?: string
  ): Promise<ApiResponse<{
    connectionId: string
    portalId: number
    portalName: string
    scopes?: string[]
  }>> => {
    const response = await api.post('/hubspot/seamless-install', {
      code,
      returnUrl,
      redirectUri: redirectUri || `${window.location.origin}/hubspot/install`
    })
    return response.data
  },

  /**
   * Marketplace install, step 1 (partner sign-in). The person is signed in;
   * the server mints a state naming them for HubSpot to hand back at step 2.
   */
  installAuthorize: async (
    returnUrl: string,
    authToken: string | null
  ): Promise<ApiResponse<{ state: string }>> => {
    const headers: Record<string, string> = {}
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`
    const response = await api.post('/hubspot/install/authorize', { returnUrl }, { headers })
    return response.data
  },

  /**
   * Marketplace install, step 2 (or the whole no-sign-in variant). Exchanges
   * HubSpot's code and attaches the portal to the signed-in account when the
   * state (or the session) names one.
   */
  installFinalize: async (
    params: { code: string; state: string | null; returnUrl: string; redirectUri: string },
    authToken: string | null
  ): Promise<ApiResponse<{
    connectionId: string
    portalId: number
    portalName: string
    scopes?: string[]
    flow: 'account' | 'standalone'
  }>> => {
    const headers: Record<string, string> = {}
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`
    const response = await api.post('/hubspot/install/finalize', params, { headers })
    return response.data
  },

  /**
   * Claim a pending HubSpot connection after user completes signup (Marketplace-first flow)
   * @param state - State token from pending connection
   */
  claimPendingConnection: async (state: string): Promise<ApiResponse<{
    connectionId: string
    portalId: string
    portalName: string
    scopes: string[]
  }>> => {
    const response = await api.post('/hubspot/claim', {
      state
    })
    return response.data
  },

  /**
   * Get user's HubSpot connections
   */
  getConnections: async (): Promise<ApiResponse<HubSpotConnection[]>> => {
    const response = await api.get('/hubspot/connections')
    return response.data
  },

  /** Get all HubSpot connections across the current team (for integration badges) */
  getTeamConnections: async (): Promise<ApiResponse<Array<{
    id: string
    userId: string
    hubspotPortalId: string
    portalName: string | null
    associatedDomains: string[]
    isActive: boolean
  }>>> => {
    const response = await api.get('/hubspot/team-connections')
    return response.data
  },

  /**
   * Validate a HubSpot connection
   */
  validateConnection: async (connectionId: string): Promise<ApiResponse<{
    isValid: boolean
    connectionId: string
  }>> => {
    const response = await api.get(`/hubspot/connections/${connectionId}/validate`)
    return response.data
  },

  /**
   * Disconnect HubSpot account
   */
  disconnectAccount: async (connectionId: string): Promise<ApiResponse> => {
    const response = await api.delete(`/hubspot/connections/${connectionId}`)
    return response.data
  },

  /**
   * List blog posts for connected portal
   */
  listBlogPosts: async (connectionId: string): Promise<ApiResponse<HubSpotBlogPost[]>> => {
    const response = await api.get('/hubspot/content/posts', {
      params: { connectionId }
    })
    return response.data
  },

  /**
   * List site pages for connected portal
   */
  listPages: async (connectionId: string): Promise<ApiResponse<HubSpotPage[]>> => {
    const response = await api.get('/hubspot/content/pages', {
      params: { connectionId }
    })
    return response.data
  },

  /**
   * List landing pages for connected portal
   */
  listLandingPages: async (connectionId: string): Promise<ApiResponse<HubSpotPage[]>> => {
    const response = await api.get('/hubspot/content/landing-pages', {
      params: { connectionId }
    })
    return response.data
  },

  /**
   * List all pages (site pages + landing pages) for connected portal
   */
  listAllPages: async (connectionId: string): Promise<ApiResponse<HubSpotPage[]>> => {
    const response = await api.get('/hubspot/content/all-pages', {
      params: { connectionId }
    })
    return response.data
  },

  /**
   * List Knowledge Base articles for connected portal
   * Note: Requires Service Hub Pro/Enterprise with cms.knowledge_base.articles.read scope
   */
  listKnowledgeArticles: async (connectionId: string): Promise<ApiResponse<HubSpotKnowledgeArticle[]>> => {
    const response = await api.get('/hubspot/content/knowledge-articles', {
      params: { connectionId }
    })
    return response.data
  },

  /**
   * Match URL to HubSpot content
   */
  matchContent: async (
    connectionId: string,
    url: string
  ): Promise<ApiResponse<HubSpotContentMatchResult[]>> => {
    const response = await api.get('/hubspot/content/match', {
      params: { connectionId, url }
    })
    return response.data
  },

  /**
   * Search content by query
   * Searches across blog posts, pages, and KB articles with pagination
   */
  searchContent: async (
    connectionId: string,
    query: string,
    options?: {
      contentType?: 'blog_post' | 'page' | 'knowledge_article' | 'all'
      limit?: number
      offset?: number
    }
  ): Promise<ApiResponse<{
    results: HubSpotContentMatchResult[]
    total: number
    hasMore: boolean
  }>> => {
    const response = await api.get('/hubspot/content/search', {
      params: {
        connectionId,
        query,
        contentType: options?.contentType || 'all',
        limit: options?.limit || 20,
        offset: options?.offset || 0
      }
    })
    return response.data
  },

  /**
   * Get content counts for a portal
   * Returns total counts of blog posts, pages, and KB articles
   */
  getContentCounts: async (connectionId: string): Promise<ApiResponse<{
    blogPosts: number
    pages: number
    knowledgeArticles: number
    total: number
  }>> => {
    const response = await api.get('/hubspot/content/counts', {
      params: { connectionId }
    })
    return response.data
  },

  /**
   * Find content by direct URL
   * Allows users to paste a HubSpot URL directly
   */
  findContentByUrl: async (
    connectionId: string,
    url: string
  ): Promise<ApiResponse<HubSpotContentMatchResult | null>> => {
    const response = await api.get('/hubspot/content/find-by-url', {
      params: { connectionId, url }
    })
    return response.data
  },

  /**
   * Get paginated content for browsing
   */
  getPaginatedContent: async (
    connectionId: string,
    contentType: 'blog_post' | 'page' | 'knowledge_article',
    options?: { limit?: number; offset?: number }
  ): Promise<ApiResponse<{
    results: HubSpotContentMatchResult[]
    total: number
    hasMore: boolean
  }>> => {
    const response = await api.get('/hubspot/content/paginated', {
      params: {
        connectionId,
        contentType,
        limit: options?.limit || 20,
        offset: options?.offset || 0
      }
    })
    return response.data
  },

  /**
   * Push schema to HubSpot content
   */
  pushSchema: async (request: PushSchemaToHubSpotRequest): Promise<ApiResponse<{
    syncJobId: string
    contentId: string
    contentType: string
  }>> => {
    const response = await api.post('/hubspot/sync/push', request)
    return response.data
  },

  /**
   * Get sync history for user
   */
  getSyncHistory: async (limit?: number, offset?: number): Promise<ApiResponse<any[]>> => {
    const response = await api.get('/hubspot/sync/history', {
      params: { limit, offset }
    })
    return response.data
  },

  /**
   * Add domain to HubSpot connection
   */
  addDomainToConnection: async (connectionId: string, domain: string): Promise<ApiResponse> => {
    const response = await api.patch(`/hubspot/connections/${connectionId}/domains/add`, {
      domain
    })
    return response.data
  },

  /**
   * Remove domain from HubSpot connection
   */
  removeDomainFromConnection: async (connectionId: string, domain: string): Promise<ApiResponse> => {
    const response = await api.patch(`/hubspot/connections/${connectionId}/domains/remove`, {
      domain
    })
    return response.data
  },

  /**
   * Find HubSpot connection by domain
   */
  findConnectionByDomain: async (domain: string): Promise<ApiResponse<HubSpotConnection | null>> => {
    const response = await api.get(`/hubspot/connections/for-domain/${domain}`)
    return response.data
  }
}
